import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import type { PageViewInput } from '@shared/schemas/activity'

function send(payload: PageViewInput) {
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
  if (!navigator.sendBeacon?.('/api/analytics/page-view', blob)) {
    // sendBeacon can fail (payload too large, disabled) — keepalive fetch
    // survives the page unloading, same as sendBeacon's whole purpose.
    fetch('/api/analytics/page-view', { method: 'POST', body: blob, keepalive: true }).catch(() => {})
  }
}

/**
 * Records how long the signed-in user spends on each route, closing out
 * the previous page's entry the moment they navigate away — powers the
 * Operator-only usage view (docs/DECISIONS.md#activity-monitoring). Mount
 * once, near the root of the authenticated app.
 */
export function usePageViewTracking(enabled: boolean) {
  const location = useLocation()
  const current = useRef<{ path: string; startedAt: number } | null>(null)

  useEffect(() => {
    if (!enabled) return

    const closeOut = () => {
      if (!current.current) return
      const endedAt = Date.now()
      const durationSeconds = Math.round((endedAt - current.current.startedAt) / 1000)
      if (durationSeconds > 0) {
        send({ path: current.current.path, startedAt: current.current.startedAt, endedAt, durationSeconds })
      }
    }

    closeOut()
    current.current = { path: location.pathname, startedAt: Date.now() }

    return closeOut
  }, [location.pathname, enabled])

  useEffect(() => {
    if (!enabled) return
    const onHide = () => {
      if (!current.current) return
      const endedAt = Date.now()
      const durationSeconds = Math.round((endedAt - current.current.startedAt) / 1000)
      if (durationSeconds > 0) {
        send({ path: current.current.path, startedAt: current.current.startedAt, endedAt, durationSeconds })
      }
      current.current = { path: current.current.path, startedAt: endedAt }
    }
    document.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [enabled])
}
